import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { AppShell } from './components/AppShell';
import { RequireAuth, RequireRole } from './components/RouteGuards';
import { qk } from './lib/api/keys';
import { fetchMe } from './lib/auth/api';
import { refreshSession } from './lib/auth/api';
import { useAuthStore } from './lib/auth/store';
import { fetchLocales, useLocaleStore } from './lib/locales';
import Activity from './routes/Activity';
import Analytics from './routes/Analytics';
import BreakingNews from './routes/BreakingNews';
import Channels from './routes/Channels';
import Dashboard from './routes/Dashboard';
import DeleteAccount from './routes/DeleteAccount';
import EventDetail from './routes/EventDetail';
import Events from './routes/Events';
import Eyewitness from './routes/Eyewitness';
import Inbox from './routes/Inbox';
import Broadcasts from './routes/Broadcasts';
import LiveContributions from './routes/LiveContributions';
import LiveContributionsArchive from './routes/LiveContributionsArchive';
import NotFound from './routes/NotFound';
import Pages from './routes/Pages';
import Playlists from './routes/Playlists';
import PostDetail from './routes/PostDetail';
import Posts from './routes/Posts';
import PresenterBrief from './routes/PresenterBrief';
import ProgrammeDetail from './routes/ProgrammeDetail';
import ContactUs from './routes/ContactUs';
import PrivacyPolicy from './routes/PrivacyPolicy';
import Programmes from './routes/Programmes';
import Schedule from './routes/Schedule';
import Settings from './routes/Settings';
import SignIn from './routes/SignIn';
import Stories from './routes/Stories';
import NewsDesk from './routes/NewsCompare';
import NowPlaying from './routes/NowPlaying';
import UserDetail from './routes/UserDetail';
import Users from './routes/Users';

export function App(): JSX.Element {
  const token = useAuthStore((s) => s.token);
  const setUser = useAuthStore((s) => s.setUser);
  const setSession = useAuthStore((s) => s.setSession);
  const hydrate = useLocaleStore((s) => s.hydrate);

  // Hydrate locales from server (always; works anonymously).
  const localesQ = useQuery({
    queryKey: qk.locales(),
    queryFn: fetchLocales,
    staleTime: 5 * 60_000,
  });
  useEffect(() => {
    if (localesQ.data) hydrate(localesQ.data);
  }, [localesQ.data, hydrate]);

  // Refresh user once after sign-in to pick up role changes (server-side promotion etc.).
  const meQ = useQuery({
    queryKey: qk.me(),
    queryFn: fetchMe,
    enabled: !!token,
    staleTime: 60_000,
  });
  useEffect(() => {
    if (meQ.data) setUser(meQ.data);
  }, [meQ.data, setUser]);

  // Sliding session: renew the JWT on load and every 12h while the tab stays
  // open — newsroom dashboards sit open for weeks, so load-time alone would
  // still lapse. Keyed on *whether* there's a token, not its value, so storing
  // the renewed token doesn't re-fire the effect. A 401 is handled by the api
  // client (clears the session → /sign-in); any other failure keeps the
  // current token for the rest of its window.
  const signedIn = !!token;
  useEffect(() => {
    if (!signedIn) return;
    const renew = (): void => {
      refreshSession()
        .then((session) => setSession(session.token, session.user))
        .catch(() => {});
    };
    renew();
    const id = window.setInterval(renew, 12 * 60 * 60_000);
    return () => window.clearInterval(id);
  }, [signedIn, setSession]);

  return (
    <Routes>
      <Route path="/sign-in" element={<SignIn />} />
      {/* Public: the app stores' web deletion path — no sign-in. */}
      <Route path="/delete-account" element={<DeleteAccount />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      {/* Public: the contact page Play's News policy requires, and the
          URL to give in the store listing + News declaration. */}
      <Route path="/contact-us" element={<ContactUs />} />
      <Route
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="inbox" element={<Inbox />} />
        <Route path="posts" element={<Posts />} />
        <Route path="posts/:slug" element={<PostDetail />} />
        <Route path="pages" element={<Pages />} />
        <Route path="pages/:slug" element={<PostDetail />} />
        <Route
          path="breaking"
          element={
            <RequireRole requiredRole="admin">
              <BreakingNews />
            </RequireRole>
          }
        />
        <Route
          path="channels"
          element={
            <RequireRole requiredRole="admin">
              <Channels />
            </RequireRole>
          }
        />
        <Route
          path="programmes"
          element={
            <RequireRole requiredRole="admin">
              <Programmes />
            </RequireRole>
          }
        />
        <Route
          path="programmes/:slug"
          element={
            <RequireRole requiredRole="admin">
              <ProgrammeDetail />
            </RequireRole>
          }
        />
        <Route
          path="playlists"
          element={
            <RequireRole requiredRole="admin">
              <Playlists />
            </RequireRole>
          }
        />
        <Route
          path="schedule"
          element={
            <RequireRole requiredRole="admin">
              <Schedule />
            </RequireRole>
          }
        />
        <Route
          path="presenter-brief"
          element={
            <RequireRole requiredRole="admin">
              <PresenterBrief />
            </RequireRole>
          }
        />
        <Route
          path="stories"
          element={
            <RequireRole requiredRole="admin">
              <Stories />
            </RequireRole>
          }
        />
        <Route
          path="eyewitness"
          element={
            <RequireRole requiredRole="admin">
              <Eyewitness />
            </RequireRole>
          }
        />
        <Route
          path="events"
          element={
            <RequireRole requiredRole="admin">
              <Events />
            </RequireRole>
          }
        />
        <Route
          path="events/:slug"
          element={
            <RequireRole requiredRole="admin">
              <EventDetail />
            </RequireRole>
          }
        />
        <Route
          path="live-contributions"
          element={
            <RequireRole requiredRole="admin">
              <LiveContributions />
            </RequireRole>
          }
        />
        <Route
          path="live-contributions/archive"
          element={
            <RequireRole requiredRole="admin">
              <LiveContributionsArchive />
            </RequireRole>
          }
        />
        <Route
          path="broadcasts"
          element={
            <RequireRole requiredRole="admin">
              <Broadcasts />
            </RequireRole>
          }
        />
        <Route
          path="users"
          element={
            <RequireRole requiredRole="admin">
              <Users />
            </RequireRole>
          }
        />
        <Route
          path="users/:id"
          element={
            <RequireRole requiredRole="admin">
              <UserDetail />
            </RequireRole>
          }
        />
        <Route
          path="now-playing"
          element={
            <RequireRole requiredRole="admin">
              <NowPlaying />
            </RequireRole>
          }
        />
        <Route
          path="activity"
          element={
            <RequireRole requiredRole="admin">
              <Activity />
            </RequireRole>
          }
        />
        <Route
          path="analytics"
          element={
            <RequireRole requiredRole="admin">
              <Analytics />
            </RequireRole>
          }
        />
        <Route
          path="news-desk"
          element={
            <RequireRole requiredRole="admin">
              <NewsDesk />
            </RequireRole>
          }
        />
        <Route
          path="settings"
          element={
            <RequireRole requiredRole="admin">
              <Settings />
            </RequireRole>
          }
        />
        <Route path="*" element={<NotFound />} />
      </Route>
      <Route path="*" element={<Navigate to="/sign-in" replace />} />
    </Routes>
  );
}
